import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHero, SectionLabel, PillButton } from "../components/ui-kit";
import { Reveal, Stagger, StaggerItem } from "../components/motion";
import { getBlog, getBlogPost } from "../lib/api";
import { ArrowRight, ArrowLeft, Clock } from "lucide-react";

const categories = ["All", "Workout", "Nutrition", "Fat Loss", "Muscle Gain", "Recovery", "Lifestyle", "Strength", "Mobility"];

export function Blog() {
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState("All");
  useEffect(() => { getBlog().then(setPosts).catch(() => {}); }, []);
  const filtered = cat === "All" ? posts : posts.filter((p) => p.category === cat);

  return (
    <div data-testid="blog-page">
      <PageHero label="The Blog" title={<>Train your <span className="text-accent">mind</span> too.</>}
        subtitle="Evidence-based articles on training, nutrition, fat loss and building a body that lasts." />

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-wrap gap-3 mb-12">
            {categories.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`blog-filter-${c.toLowerCase().replace(/ /g,'-')}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${cat === c ? "bg-accent text-white" : "glass hover:text-accent"}`}>
                {c}
              </button>
            ))}
          </div>
          <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((p) => (
              <StaggerItem key={p.id}>
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="block group">
                  <div className="rounded-2xl overflow-hidden glass hover-lift h-full">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3 text-xs uppercase tracking-widest">
                        <span className="text-accent font-bold">{p.category}</span>
                        <span className="flex items-center gap-1 text-muted-foreground"><Clock className="w-3 h-3" /> {p.read}</span>
                      </div>
                      <h3 className="font-heading font-bold text-xl leading-snug mb-2 group-hover:text-accent transition-colors">{p.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{p.excerpt}</p>
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  useEffect(() => { getBlogPost(slug).then(setPost).catch(() => setPost(false)); }, [slug]);

  if (post === false) return <div className="pt-48 text-center"><p className="text-muted-foreground">Article not found.</p><Link to="/blog" className="text-accent">Back to blog</Link></div>;
  if (!post) return <div className="pt-48 text-center text-muted-foreground">Loading...</div>;

  return (
    <div data-testid="blog-post-page">
      <section className="relative pt-40 pb-16 md:pt-48">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Reveal>
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent mb-8"><ArrowLeft className="w-4 h-4" /> All Articles</Link>
            <SectionLabel>{post.category} • {post.read}</SectionLabel>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight leading-[1.05] mb-8">{post.title}</h1>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl overflow-hidden mb-10">
              <img src={post.image} alt={post.title} className="w-full h-[420px] object-cover" />
            </div>
            <p className="text-xl text-muted-foreground leading-relaxed mb-6 font-medium">{post.excerpt}</p>
            <p className="text-lg leading-relaxed">{post.body}</p>
          </Reveal>
          <div className="mt-14 pt-10 border-t border-border text-center">
            <h3 className="text-2xl font-black tracking-tight mb-6">Want a plan built for you?</h3>
            <PillButton to="/coaching" testid="blogpost-cta">Apply For Coaching <ArrowRight className="w-4 h-4 ml-2" /></PillButton>
          </div>
        </div>
      </section>
    </div>
  );
}
